import { useTranslation } from 'react-i18next';
import { useSkinStore } from '@/shared/stores/useSkinStore';
import type { SkinId } from '@/features/game/components/board/skins/types';
import type {
  SkinComponentType,
  SkinMetadata
} from '@/features/game/components/board/skins/metadata';
import { Button } from '@/shared/components/Button';
import { shopIcon } from '@/assets';
import { SkinPreview } from './SkinPreview';

interface SkinCardProps {
  skin: SkinMetadata;
  componentType: SkinComponentType;
  isSelected: boolean;
  onSelect: (componentType: SkinComponentType, skinId: SkinId) => void;
  onStartTrial?: (componentType: SkinComponentType, skinId: SkinId) => void;
  onOpenShop?: () => void;
  isOwned: boolean;
  isTrialed: boolean;
  isLoading?: boolean;
}

type CardState = 'owned' | 'trial-available' | 'trial-active' | 'locked';

function getCardState(
  skinId: SkinId,
  isOwned: boolean,
  isTrialed: boolean,
  activeTrialSkinIds: SkinId[],
  consumedTrialSkinIds: SkinId[]
): CardState {
  if (isOwned) return 'owned';
  if (activeTrialSkinIds.includes(skinId)) return 'trial-active';
  if (consumedTrialSkinIds.includes(skinId) || isTrialed) return 'locked';
  return 'trial-available';
}

export function SkinCard({
  skin,
  componentType,
  isSelected,
  onSelect,
  onStartTrial,
  onOpenShop,
  isOwned,
  isTrialed,
  isLoading
}: SkinCardProps) {
  const { t } = useTranslation();
  const activeTrialSkinIds = useSkinStore((s) => s.activeTrialSkinIds);
  const consumedTrialSkinIds = useSkinStore((s) => s.consumedTrialSkinIds);

  const cardState = isLoading
    ? 'owned'
    : getCardState(skin.id, isOwned, isTrialed, activeTrialSkinIds, consumedTrialSkinIds);

  const handleClick = () => {
    switch (cardState) {
      case 'owned':
      case 'trial-active':
        onSelect(componentType, skin.id);
        break;
      case 'trial-available':
        onStartTrial?.(componentType, skin.id);
        break;
      case 'locked':
        onOpenShop?.();
        break;
    }
  };

  const getVariant = (): 'yellow' | 'blue' | 'grey' => {
    if (isSelected && (cardState === 'owned' || cardState === 'trial-active')) return 'yellow';
    if (cardState === 'locked') return 'grey';
    return 'blue';
  };

  return (
    <Button
      variant={getVariant()}
      className={`aspect-square w-full relative ${cardState === 'locked' ? 'opacity-60' : ''}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-center size-full p-[10%]">
        <SkinPreview skinId={skin.id} componentType={componentType} />
      </div>

      {/* Trial available badge */}
      {cardState === 'trial-available' && (
        <span className="absolute top-0 right-0 bg-green-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-bl-lg rounded-tr-lg leading-none">
          {t('skins.trial')}
        </span>
      )}

      {/* Trial active badge */}
      {cardState === 'trial-active' && (
        <span className="absolute top-0 right-0 bg-amber-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-bl-lg rounded-tr-lg leading-none">
          {t('skins.trial')}
        </span>
      )}

      {/* Shop icon for locked skins */}
      {cardState === 'locked' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-[inherit]">
          <img
            src={shopIcon}
            alt={t('skins.locked')}
            className="w-6 h-6 object-contain drop-shadow-md"
          />
        </div>
      )}
    </Button>
  );
}
